import React, { useEffect } from 'react';
import { toFlag } from 'pano-utils';
import { ImageType, type TourTip } from '@types';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Slider from '@components/Slider';
import Title from '@components/Title';
import useTourEditing from '@hooks/useTourEditing';
import TourTipImage from '@components/TourTipImage';
import { $editingTourTip } from '@stores/editingStores';
import { selectLibImages } from '../../mediaLib';
import PanelBase from '../PanelBase';
import MainToggle from '../components/MainToggle';
import type { PanelBaseProps } from '../types';

function TourTipBase({ defaultValue, onConfirm }: PanelBaseProps<TourTip>) {
  const { state, setState, isChanged, defaultState } = useTourEditing(
    defaultValue,
    {
      enabled: 1,
      pcMediaId: 0,
      pcMediaUrl: '',
      mobileMediaId: 0,
      mobileMediaUrl: '',
      duration: 2,
    }
  );

  useEffect(() => {
    $editingTourTip.api.set(state);
  }, [state]);

  const isOn = !!state.enabled;

  const handleConfirm = () => {
    if (isOn) {
      onConfirm(state);
    } else {
      onConfirm({ ...defaultState, enabled: 0 });
    }
  };

  const renderSetting = ({
    title,
    idKey,
    urlKey,
  }: {
    title: string;
    idKey: 'pcMediaId' | 'mobileMediaId';
    urlKey: 'pcMediaUrl' | 'mobileMediaUrl';
  }) => {
    const url = state[urlKey];
    return (
      <Collapse title={title}>
        <ImageContainer
          className="h-115"
          placeholder={
            <TourTipImage
              isMobile={idKey === 'mobileMediaId'}
              className="max-h-full max-w-full"
            />
          }
          onDeleteClick={() => {
            setState((draft) => {
              draft[idKey] = 0;
              draft[urlKey] = '';
            });
          }}
        >
          {!!url && (
            <Image
              src={url}
              type={ImageType.media}
              className="max-h-full max-w-full"
            />
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸：300 X 300</Tip>
          <Button
            size="sm"
            onClick={async () => {
              const { libItems } = await selectLibImages();
              if (!libItems) return;
              setState((draft) => {
                draft[idKey] = libItems[0].id;
                draft[urlKey] = libItems[0].url;
              });
            }}
          >
            添加图片
          </Button>
        </div>
      </Collapse>
    );
  };

  return (
    <PanelBase
      title={<Title>开场提示</Title>}
      isChanged={isChanged}
      onConfirm={handleConfirm}
      onHelpClick={() => {}}
    >
      <MainToggle
        isOn={isOn}
        onToggle={(bool) =>
          setState((draft) => {
            draft.enabled = toFlag(bool);
          })
        }
      />
      {isOn && (
        <>
          {renderSetting({
            idKey: 'pcMediaId',
            urlKey: 'pcMediaUrl',
            title: '桌面端操作提示',
          })}
          {renderSetting({
            idKey: 'mobileMediaId',
            urlKey: 'mobileMediaUrl',
            title: '移动端操作提示',
          })}
          <Collapse title="显示时长设置">
            <Slider
              min={1}
              max={10}
              valToString={(val) => `${val}秒`}
              value={state.duration}
              onChange={(val) => {
                setState((draft) => {
                  draft.duration = val;
                });
              }}
            >
              <Slider.Header title="当前时长：" />
              <Slider.Bar />
              <Slider.Footer />
            </Slider>
          </Collapse>
        </>
      )}
    </PanelBase>
  );
}

export default TourTipBase;
